/**
 * ImportFromDriveButton — lets the workspace owner pull existing files from
 * their own Google Drive into OVACS via the Google Picker.
 *
 * Why the Picker (and not "watch the OVACS folder"): OVACS uses the drive.file
 * scope, which can't see files it didn't create. The Picker grants per-file
 * access to exactly the files the user chooses — so we can import them while
 * staying within drive.file (no restricted scope / CASA needed). The backend
 * copies each picked file into the OVACS folder and registers it as a Document.
 *
 * Requires GOOGLE_PICKER_API_KEY server-side; if it's not configured the
 * picker-config endpoint returns 503 and we surface a clear message.
 */

import { useState } from 'react'
import { HiOutlineCloud } from 'react-icons/hi2'
import { googleDriveAPI } from '../../services/integrations/googleDrive'
import { useToast } from '../common/ToastContainer'
import { loadGooglePicker } from './loadGooglePicker'

export default function ImportFromDriveButton({
  caseId = null,
  sessionId = null,
  clientId = null,
  groupId = null,
  securityLevel = 'green',
  onImported,
  className = '',
  disabled = false,
}) {
  const { showSuccess, showError, showWarning } = useToast()
  const [busy, setBusy] = useState(false)

  const unwrap = (res) => (res?.data?.data ?? res?.data ?? null)

  const handleImport = async (fileIds) => {
    const res = await googleDriveAPI.importFiles({
      file_ids: fileIds,
      security_level: securityLevel,
      case_id: caseId || undefined,
      session_id: sessionId || undefined,
      client_id: clientId || undefined,
      group_id: groupId || undefined,
    })
    if (!res?.success) {
      showError(res?.error?.message || 'Failed to import files from Drive.', 'Import from Drive')
      return
    }
    const payload = unwrap(res) || {}
    const created = payload.created || []
    const failed = payload.failed || []
    if (created.length) {
      showSuccess(
        `Imported ${created.length} file${created.length === 1 ? '' : 's'} from Google Drive.`,
        'Import from Drive',
      )
    }
    if (failed.length) {
      showWarning(
        `${failed.length} file${failed.length === 1 ? '' : 's'} could not be imported.`,
        'Import from Drive',
      )
    }
    if (created.length) onImported?.(created)
  }

  const openPicker = async () => {
    if (busy || disabled) return
    setBusy(true)
    try {
      const res = await googleDriveAPI.pickerConfig()
      if (!res?.success) {
        const msg = res?.error?.message || 'Import from Drive is unavailable right now.'
        // 503 => not configured on the server; guide accordingly.
        showWarning(msg, 'Import from Drive')
        return
      }
      const cfg = unwrap(res)
      if (!cfg?.access_token || !cfg?.api_key) {
        showWarning('Import from Drive is not configured yet.', 'Import from Drive')
        return
      }

      await loadGooglePicker()
      const picker = window.google.picker
      const view = new picker.DocsView(picker.ViewId.DOCS)
        .setIncludeFolders(false)
        .setSelectFolderEnabled(false)

      const builder = new picker.PickerBuilder()
        .enableFeature(picker.Feature.MULTISELECT_ENABLED)
        .setOAuthToken(cfg.access_token)
        .setDeveloperKey(cfg.api_key)
        .addView(view)
        .setOrigin(`${window.location.protocol}//${window.location.host}`)
        .setCallback(async (data) => {
          if (data.action !== picker.Action.PICKED) return
          const fileIds = (data.docs || []).map((d) => d.id).filter(Boolean)
          if (!fileIds.length) return
          setBusy(true)
          try {
            await handleImport(fileIds)
          } catch (e) {
            showError(e?.message || 'Failed to import files from Drive.', 'Import from Drive')
          } finally {
            setBusy(false)
          }
        })

      if (cfg.app_id) builder.setAppId(String(cfg.app_id))
      builder.build().setVisible(true)
    } catch (e) {
      showError(e?.message || 'Could not open the Google Picker.', 'Import from Drive')
    } finally {
      setBusy(false)
    }
  }

  return (
    <button
      type="button"
      onClick={openPicker}
      disabled={busy || disabled}
      className={
        className ||
        'inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border border-info/30 bg-info-soft text-brand hover:bg-info-soft/70 disabled:opacity-60 disabled:cursor-not-allowed transition-colors'
      }
      title="Import existing files from your Google Drive into OVACS"
    >
      <HiOutlineCloud className="w-4 h-4" />
      {busy ? 'Importing…' : 'Import from Drive'}
    </button>
  )
}
